import { css } from 'aphrodite';
import * as React from 'react';
import { IAccount } from '../../../extViewmodels';
import { ICalendarMeetingWithTranscripts, ITranscriptMatch } from '../../../viewmodels/CalendarViewModels';
import { LoadingSpinner } from '../../../web/components/LoadingSpinner';
import { useMeetingNoteRecording } from '../../hooks/useMeetingNoteRecording';
import { MeetingNoteForm } from './MeetingNoteForm';
import { PostMeetingConfirmations } from './PostMeetingConfirmations';
import { styleSheet } from './styles';

interface IProps {
	/** The transcript match to record a note for */
	transcript: ITranscriptMatch;
	/** The meeting with transcripts data (for attendee info) */
	meetingWithTranscripts: ICalendarMeetingWithTranscripts;
	/** Pre-selected account */
	preSelectedAccount?: IAccount | null;
	/** Called when the flow completes or is cancelled */
	onComplete: () => void;
}

export const MeetingNoteRecordingFlow: React.FC<IProps> = ({
	transcript,
	meetingWithTranscripts,
	preSelectedAccount,
	onComplete,
}) => {
	const recording = useMeetingNoteRecording({
		preSelectedAccount,
		onComplete,
	});

	// This component is always unmounted/remounted for each new transcript
	// (parent only renders it when a transcript is selected), so we intentionally
	// run startRecording only once on mount.
	const hasStartedRef = React.useRef(false);
	React.useEffect(() => {
		if (!hasStartedRef.current) {
			hasStartedRef.current = true;
			recording.startRecording(transcript, meetingWithTranscripts);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	if (recording.phase === 'idle' || recording.phase === 'summarizing') {
		return (
			<div className={css(styleSheet.loadingContainer)}>
				<LoadingSpinner type='large' />
				<span className={css(styleSheet.loadingText)}>Generating meeting note summary...</span>
			</div>
		);
	}

	if (recording.phase === 'form') {
		return (
			<MeetingNoteForm
				selectedAccount={recording.selectedAccount}
				accountSearchValue={recording.accountSearchValue}
				accounts={recording.accounts}
				accountsQueryLoading={recording.accountsQuery.isLoading}
				meetingNoteContentState={recording.meetingNoteContentState}
				meetingType={recording.meetingType}
				meetingStatus={recording.meetingStatus}
				accountStage={recording.accountStage}
				duration={recording.duration}
				isOnboarding={recording.isOnboarding}
				nextMeetingDate={recording.nextMeetingDate}
				showNextMeetingDatePicker={recording.showNextMeetingDatePicker}
				validationErrors={recording.validationErrors}
				isSavingNote={recording.isSavingNote}
				csmUserForCcNotice={recording.csmUserForCcNotice}
				onAccountChange={account => {
					recording.setSelectedAccount(account);
					if (account) {
						recording.setAccountStage(account.additionalInfo?.stage || null);
					}
				}}
				onAccountSearchValueChange={recording.handleAccountSearchValueChange}
				onClearAccount={() => {
					recording.setSelectedAccount(null);
					recording.handleAccountSearchValueChange('');
					recording.setAccountStage(null);
				}}
				onContentStateChanged={recording.setMeetingNoteContentState}
				onMeetingTypeChange={recording.setMeetingType}
				onMeetingStatusChange={recording.setMeetingStatus}
				onAccountStageChange={recording.setAccountStage}
				onDurationChange={recording.setDuration}
				onIsOnboardingChange={recording.setIsOnboarding}
				onNextMeetingDateChange={recording.setNextMeetingDate}
				onShowNextMeetingDatePickerChange={recording.setShowNextMeetingDatePicker}
				onSave={recording.handleSaveMeetingNote}
				onSkip={recording.handleSkipToConfirmation}
			/>
		);
	}

	if (recording.phase === 'confirmations') {
		return (
			<PostMeetingConfirmations
				didSkipMeetingNote={recording.didSkipMeetingNote}
				selectedAccount={recording.selectedAccount}
				activeConfirmationTab={recording.activeConfirmationTab}
				completedTabs={recording.completedTabs}
				onTabChange={recording.handleConfirmationTabChange}
				// Web Team
				showWebTeamConfirmation={recording.showWebTeamConfirmation}
				webTeamSummary={recording.webTeamSummary}
				isGeneratingWebTeamSummary={recording.isGeneratingWebTeamSummary}
				webTeamBusinessType={recording.webTeamBusinessType}
				webTeamLocation={recording.webTeamLocation}
				webTeamWebsiteUrl={recording.webTeamWebsiteUrl}
				onWebTeamBusinessTypeChange={recording.setWebTeamBusinessType}
				onWebTeamLocationChange={recording.setWebTeamLocation}
				onWebTeamWebsiteUrlChange={recording.setWebTeamWebsiteUrl}
				onGenerateWebTeamSummary={recording.handleGenerateWebTeamSummary}
				onSkipWebTeamSummary={recording.handleSkipWebTeamSummary}
				onCopyWebTeamSummary={recording.handleCopyWebTeamSummary}
				// Content Team
				showContentTeamConfirmation={recording.showContentTeamConfirmation}
				contentTeamSummary={recording.contentTeamSummary}
				isGeneratingContentTeamSummary={recording.isGeneratingContentTeamSummary}
				contentTeamWebsiteUrl={recording.contentTeamWebsiteUrl}
				contentTeamContentState={recording.contentTeamContentState}
				isSubmittingContentTeamNote={recording.isSubmittingContentTeamNote}
				onContentTeamWebsiteUrlChange={recording.setContentTeamWebsiteUrl}
				contentTeamOutputMix={recording.contentTeamOutputMix}
				onContentTeamOutputMixChange={recording.setContentTeamOutputMix}
				contentTeamStartMonth={recording.contentTeamStartMonth}
				onContentTeamStartMonthChange={recording.setContentTeamStartMonth}
				contentTeamMonthCount={recording.contentTeamMonthCount}
				onContentTeamMonthCountChange={recording.setContentTeamMonthCount}
				contentTeamNewsletterTemplateId={recording.contentTeamNewsletterTemplateId}
				onContentTeamNewsletterTemplateIdChange={recording.setContentTeamNewsletterTemplateId}
				onContentTeamContentStateChange={recording.setContentTeamContentState}
				onGenerateContentTeamSummary={recording.handleGenerateContentTeamSummary}
				onSkipContentTeamSummary={recording.handleSkipContentTeamSummary}
				onSubmitContentTeamNote={recording.handleSubmitContentTeamNote}
				// Account Profile
				showAccountProfileConfirmation={recording.showAccountProfileConfirmation}
				accountProfileWebsiteUrl={recording.accountProfileWebsiteUrl}
				isGeneratingAccountProfile={recording.isGeneratingAccountProfile}
				generatedBusinessDescription={recording.generatedBusinessDescription}
				generatedKeywords={recording.generatedKeywords}
				generatedContentGoals={recording.generatedContentGoals}
				generatedLocation={recording.generatedLocation}
				generatedAddress={recording.generatedAddress}
				generatedPhoneNumber={recording.generatedPhoneNumber}
				generatedPronouns={recording.generatedPronouns}
				generatedTonePreference={recording.generatedTonePreference}
				isSavingAccountProfile={recording.isSavingAccountProfile}
				onAccountProfileWebsiteUrlChange={recording.setAccountProfileWebsiteUrl}
				onBusinessDescriptionChange={recording.setGeneratedBusinessDescription}
				onKeywordsChange={recording.setGeneratedKeywords}
				onContentGoalsChange={recording.setGeneratedContentGoals}
				onLocationChange={recording.setGeneratedLocation}
				onAddressChange={recording.setGeneratedAddress}
				onPhoneNumberChange={recording.setGeneratedPhoneNumber}
				onPronounsChange={recording.setGeneratedPronouns}
				onTonePreferenceChange={recording.setGeneratedTonePreference}
				onGenerateAccountProfile={recording.handleGenerateAccountProfile}
				onSaveAccountProfile={recording.handleSaveAccountProfile}
				onSkipAccountProfile={recording.handleSkipAccountProfile}
				// Brand Settings
				showBrandSettingsConfirmation={recording.showBrandSettingsConfirmation}
				brandSettingsWebsiteUrl={recording.brandSettingsWebsiteUrl}
				brandSettingsWebsiteUrlSaved={recording.brandSettingsWebsiteUrlSaved}
				isSavingBrandSettingsWebsiteUrl={recording.isSavingBrandSettingsWebsiteUrl}
				onBrandSettingsWebsiteUrlChange={recording.setBrandSettingsWebsiteUrl}
				onSaveBrandSettingsWebsiteUrl={recording.handleSaveBrandSettingsWebsiteUrl}
				onCompleteBrandSettings={recording.handleCompleteBrandSettings}
				// Industry & Subvertical
				showSubverticalSettingsConfirmation={recording.showSubverticalSettingsConfirmation}
				onCompleteSubverticalSettings={recording.handleCompleteSubverticalSettings}
				// Recap Email
				showRecapEmailConfirmation={recording.showRecapEmailConfirmation}
				recapEmailContentState={recording.recapEmailContentState}
				recapEmailSubject={recording.recapEmailSubject}
				recapEmailRecipients={recording.recapEmailRecipients}
				isGeneratingRecapEmail={recording.isGeneratingRecapEmail}
				isSendingRecapEmail={recording.isSendingRecapEmail}
				onRecapEmailContentStateChange={recording.setRecapEmailContentState}
				onRecapEmailSubjectChange={recording.setRecapEmailSubject}
				onRecapEmailRecipientsChange={recording.setRecapEmailRecipients}
				onGenerateRecapEmail={recording.handleGenerateRecapEmail}
				onSendRecapEmail={recording.handleSendRecapEmail}
				onSkipRecapEmail={recording.handleSkipRecapEmail}
			/>
		);
	}

	// phase === 'done' - should not normally render, onComplete handles it
	return null;
};
